/**
 * PanelOverlayContext
 *
 * Context for managing flyout menus and overlay content within PanelSplitView.
 * Allows sidebar items to register flyouts that render in the overlay layer,
 * extending beyond the sidebar boundaries into the content area.
 *
 * @example
 * ```tsx
 * // In a sidebar item component
 * const { showFlyout, hideFlyout } = usePanelOverlay();
 *
 * const handleClick = (e: React.MouseEvent) => {
 *   const rect = e.currentTarget.getBoundingClientRect();
 *   showFlyout({
 *     id: 'my-flyout',
 *     content: <MyFlyoutContent />,
 *     anchorY: rect.top + rect.height / 2,
 *   });
 * };
 * ```
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

/**
 * Configuration for a flyout item
 */
export interface FlyoutConfig {
  /** Unique identifier for this flyout */
  id: string;
  /** React content to render in the flyout */
  content: React.ReactNode;
  /** Vertical anchor position (Y coordinate relative to viewport) */
  anchorY: number;
  /** Optional horizontal offset from sidebar edge */
  offsetX?: number;
  /** Whether to show arrow pointing to sidebar */
  showArrow?: boolean;
  /** Optional callback when flyout is closed */
  onClose?: () => void;
  /** Whether this flyout should trap focus */
  trapFocus?: boolean;
  /**
   * Whether to render without box styling (no background, shadow, or border-radius).
   * Use for hints and other content that should appear without a container.
   * @default false
   */
  transparent?: boolean;
  /**
   * Whether the flyout should persist (not close on click-outside or Escape).
   * Use for hints that should only close programmatically.
   * @default false
   */
  persistent?: boolean;
}

/**
 * Context value for panel overlay management
 */
export interface PanelOverlayContextValue {
  /** Currently active flyouts */
  flyouts: Map<string, FlyoutConfig>;
  /** Show a flyout with the given configuration */
  showFlyout: (config: FlyoutConfig) => void;
  /** Hide a flyout by ID */
  hideFlyout: (id: string) => void;
  /** Hide all flyouts */
  hideAllFlyouts: () => void;
  /** Check if a flyout is visible */
  isFlyoutVisible: (id: string) => boolean;
  /** Update an existing flyout's position */
  updateFlyoutPosition: (id: string, anchorY: number) => void;
  /** Whether the panel is currently collapsed */
  isCollapsed: boolean;
  /** Set the collapsed state (used internally by PanelSplitView) */
  setCollapsed: (collapsed: boolean) => void;
}

const PanelOverlayContext = createContext<PanelOverlayContextValue | null>(
  null,
);

/**
 * Hook to access panel overlay functionality
 * @throws Error if used outside PanelOverlayProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePanelOverlay(): PanelOverlayContextValue {
  const context = useContext(PanelOverlayContext);
  if (!context) {
    throw new Error(
      "usePanelOverlay must be used within a PanelSplitView component",
    );
  }
  return context;
}

/**
 * Optional hook that returns null if not in a PanelSplitView
 * Useful for components that may or may not be in a panel context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePanelOverlayOptional(): PanelOverlayContextValue | null {
  return useContext(PanelOverlayContext);
}

interface PanelOverlayProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for panel overlay context
 * Used internally by PanelSplitView
 */
export function PanelOverlayProvider({ children }: PanelOverlayProviderProps) {
  const [flyouts, setFlyouts] = useState<Map<string, FlyoutConfig>>(
    () => new Map(),
  );
  const [isCollapsed, setCollapsed] = useState(false);

  const showFlyout = useCallback((config: FlyoutConfig) => {
    setFlyouts((prev) => {
      const next = new Map(prev);
      next.set(config.id, config);
      return next;
    });
  }, []);

  const hideFlyout = useCallback((id: string) => {
    setFlyouts((prev) => {
      const flyout = prev.get(id);
      if (flyout?.onClose) {
        flyout.onClose();
      }
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const hideAllFlyouts = useCallback(() => {
    setFlyouts((prev) => {
      // Call onClose for each flyout
      prev.forEach((flyout) => {
        if (flyout.onClose) {
          flyout.onClose();
        }
      });
      return new Map();
    });
  }, []);

  // Use a ref to access flyouts without causing re-renders
  const flyoutsRef = useRef(flyouts);
  flyoutsRef.current = flyouts;

  const isFlyoutVisible = useCallback((id: string) => {
    return flyoutsRef.current.has(id);
  }, []);

  const updateFlyoutPosition = useCallback((id: string, anchorY: number) => {
    setFlyouts((prev) => {
      const flyout = prev.get(id);
      if (!flyout) return prev;
      const next = new Map(prev);
      next.set(id, { ...flyout, anchorY });
      return next;
    });
  }, []);

  const value = useMemo<PanelOverlayContextValue>(
    () => ({
      flyouts,
      showFlyout,
      hideFlyout,
      hideAllFlyouts,
      isFlyoutVisible,
      updateFlyoutPosition,
      isCollapsed,
      setCollapsed,
    }),
    [
      flyouts,
      showFlyout,
      hideFlyout,
      hideAllFlyouts,
      isFlyoutVisible,
      updateFlyoutPosition,
      isCollapsed,
    ],
  );

  return (
    <PanelOverlayContext.Provider value={value}>
      {children}
    </PanelOverlayContext.Provider>
  );
}

export { PanelOverlayContext };
